import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import type { CategoryRow, SectionRow } from "@/lib/categories.functions";
import {
  getOnsitePageData,
  type OnsiteAmenity,
} from "@/lib/onsite.functions";
import {
  getNearbyPageData,
  type NearbyAttraction,
} from "@/lib/nearby.functions";
import { useGalleryGestureLock } from "@/hooks/use-gallery-gesture-lock";
import heroFallbackAsset from "@/assets/path-hero.jpg.asset.json";

const heroFallback = heroFallbackAsset.url;

type Props = {
  category: CategoryRow;
  sections: SectionRow[];
  appendCta?: boolean;
  experienceSlug?: string | null;
  initialThingsTab?: "onsite" | "nearby";
  isActive?: boolean;
};

type Tab = "nearby" | "onsite";

function ThingsToDoCategoryPageImpl({ experienceSlug, initialThingsTab, isActive = true }: Props) {
  const [tab, setTab] = useState<Tab>(initialThingsTab ?? "nearby");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [openItem, setOpenItem] = useState<
    | { kind: "onsite"; item: OnsiteAmenity }
    | { kind: "nearby"; item: NearbyAttraction }
    | null
  >(null);
  useGalleryGestureLock(openItem !== null);

  const scrollToPageTop = useCallback((fromElement?: HTMLElement | null) => {
    if (typeof window === "undefined") return;

    const root =
      rootRef.current ??
      fromElement?.closest<HTMLDivElement>("[data-things-to-do-page]") ??
      null;
    let node = root?.parentElement ?? null;
    let didScroll = false;
    while (node) {
      const style = window.getComputedStyle(node);
      const canScrollY = /(auto|scroll)/.test(style.overflowY);
      if (canScrollY && node.scrollHeight > node.clientHeight) {
        node.scrollTop = 0;
        node.scrollTo({ top: 0, left: 0, behavior: "auto" });
        didScroll = true;
      }
      node = node.parentElement;
    }

    if (!didScroll) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.body.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, []);

  const switchTab = useCallback(
    (key: Tab, trigger: HTMLElement) => {
      const resetScroll = () => scrollToPageTop(trigger);
      setTab(key);
      resetScroll();
      window.requestAnimationFrame(resetScroll);
      window.requestAnimationFrame(() => window.requestAnimationFrame(resetScroll));
      window.setTimeout(resetScroll, 75);
      window.setTimeout(resetScroll, 250);
    },
    [scrollToPageTop],
  );

  useLayoutEffect(() => {
    scrollToPageTop();
  }, [scrollToPageTop, tab]);

  const fetchOnsite = useServerFn(getOnsitePageData);
  const fetchNearby = useServerFn(getNearbyPageData);

  const { data: onsiteData } = useQuery({
    queryKey: ["onsite", "page", experienceSlug ?? null],
    queryFn: () =>
      experienceSlug
        ? fetchOnsite({ data: { experience_slug: experienceSlug } })
        : fetchOnsite(),
  });
  const { data: nearbyData } = useQuery({
    queryKey: ["nearby", "page", experienceSlug ?? null],
    queryFn: () =>
      experienceSlug
        ? fetchNearby({ data: { experience_slug: experienceSlug } })
        : fetchNearby(),
  });

  const onsiteContent = onsiteData?.content ?? null;
  const nearbyContent = nearbyData?.content ?? null;
  const amenities = onsiteData?.amenities ?? [];
  const attractions = nearbyData?.attractions ?? [];

  const heroes = {
    onsite: {
      image: onsiteContent?.hero_image_url || heroFallback,
      kicker: onsiteContent?.hero_kicker || "On the Land",
      title: onsiteContent?.hero_title || "Onsite",
      tabLabel:
        onsiteContent?.tab_label ||
        onsiteContent?.hero_title ||
        "Onsite",
      subtitle:
        onsiteContent?.hero_subtitle ||
        "Everything you need without ever leaving the property.",
    },
    nearby: {
      image: nearbyContent?.hero_image_url || heroFallback,
      kicker: nearbyContent?.hero_kicker || "Beyond the Property",
      title: nearbyContent?.hero_title || "Nearby",
      tabLabel:
        nearbyContent?.tab_label ||
        nearbyContent?.hero_title ||
        "Nearby",
      subtitle:
        nearbyContent?.hero_subtitle ||
        "Adventures and discoveries just a short drive from your suite.",
    },
  };

  const introBody =
    tab === "onsite"
      ? onsiteContent?.intro_body || ""
      : nearbyContent?.intro_body || "";

  return (
    <div
      ref={rootRef}
      data-things-to-do-page
      className="relative w-full bg-background"
      style={{ overflowAnchor: "none" }}
    >
      {/* Hero with crossfade between tabs */}
      <section className="mobile-hero relative w-full overflow-hidden vignette aspect-[4/5] sm:aspect-[16/10] max-h-[78svh]">
        {(["nearby", "onsite"] as const).map((key) => {
          const h = heroes[key];
          const active = tab === key;
          return (
            <div
              key={key}
              className="absolute inset-0 transition-opacity duration-500 ease-out"
              style={{ opacity: active ? 1 : 0 }}
              aria-hidden={!active}
            >
              <img
                src={h.image}
                alt={h.title}
                className="absolute inset-0 w-full h-full object-cover ken-burns"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                draggable={false}
              />
              <div className="mobile-hero-scrim" />
              <div className="mobile-hero-copy px-7">
                <div className="mobile-hero-kicker text-[10px] tracking-[0.4em] uppercase mb-3 text-shadow-soft">
                  {h.kicker}
                </div>
                {isActive && active ? (
                  <h1 className="mobile-hero-title font-serif text-4xl sm:text-5xl leading-[1.05] text-shadow-soft max-w-[16ch]">
                    {h.title}
                  </h1>
                ) : (
                  <div className="mobile-hero-title font-serif text-4xl sm:text-5xl leading-[1.05] text-shadow-soft max-w-[16ch]">
                    {h.title}
                  </div>
                )}
                <p className="mobile-hero-subtitle mt-3 text-sm sm:text-base font-light max-w-[42ch] text-shadow-soft">
                  {h.subtitle}
                </p>
              </div>
            </div>
          );
        })}
      </section>

      {/* Sticky tab switcher — directly below hero, sticks under the top bar */}
      <div className="sticky top-14 z-20 bg-background/90 backdrop-blur-md border-b border-foreground/10">
        <div className="max-w-2xl lg:max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <div
            role="tablist"
            aria-label="Things to do sections"
            className="grid grid-cols-2 gap-2 rounded-full border border-foreground/15 bg-muted/30 p-1"
          >
            {(["nearby", "onsite"] as const).map((key) => {
              const active = tab === key;
              return (
                <button
                  key={key}
                  role="tab"
                  aria-selected={active}
                  type="button"
                  onClick={(event) => switchTab(key, event.currentTarget)}
                  className={
                    "rounded-full py-2 text-[11px] tracking-[0.3em] uppercase font-light transition-colors " +
                    (active
                      ? "bg-foreground text-background"
                      : "text-foreground/70 hover:text-foreground")
                  }
                >
                  {heroes[key].tabLabel}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Intro */}
      {introBody && (
        <section className="px-6 sm:px-8 py-14 sm:py-20 max-w-3xl mx-auto text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-6">
            {tab === "onsite" ? "The Property" : "The Area"}
          </div>
          <p className="font-serif text-lg sm:text-2xl text-foreground/90 leading-relaxed whitespace-pre-line">
            {introBody}
          </p>
        </section>
      )}

      {/* Items grid */}
      <section className="px-4 sm:px-6 pb-20 max-w-2xl lg:max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-3">
            {tab === "onsite" ? "Amenities" : "Attractions"}
          </div>
          <h3 className="font-serif text-3xl sm:text-4xl text-foreground">
            {tab === "onsite" ? "Included in your stay" : "Worth the drive"}
          </h3>
          <p className="mt-2 text-sm text-foreground/60 font-light whitespace-pre-line">
            Tap any card for details and photos.{"\n"}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {tab === "onsite"
            ? amenities.map((am) => (
                <ItemCard
                  key={am.id}
                  label={am.label}
                  cover={am.cover_image_url}
                  onClick={() => setOpenItem({ kind: "onsite", item: am })}
                />
              ))
            : attractions.map((at) => (
                <ItemCard
                  key={at.id}
                  label={at.label}
                  cover={at.cover_image_url}
                  onClick={() => setOpenItem({ kind: "nearby", item: at })}
                />
              ))}
          {tab === "onsite" && amenities.length === 0 && (
            <p className="col-span-full text-center text-sm text-foreground/50 italic py-10">
              Amenities coming soon.
            </p>
          )}
          {tab === "nearby" && attractions.length === 0 && (
            <p className="col-span-full text-center text-sm text-foreground/50 italic py-10">
              Attractions coming soon.
            </p>
          )}
        </div>
      </section>

      {/* Detail dialog */}
      <Dialog
        open={openItem !== null}
        onOpenChange={(o) => !o && setOpenItem(null)}
      >
        <DialogContent hideCloseButton className="gallery-zoom-lock max-w-lg lg:max-w-[1200px] bg-background border-foreground/15 max-h-[85svh] overflow-y-auto">
          {openItem && (
            <>
              <button
                type="button"
                onClick={() => setOpenItem(null)}
                aria-label="Close"
                className="sticky top-0 z-20 justify-self-end -mt-2 -mr-2 inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/70 border border-white/25 text-foreground hover:bg-black/90 transition shadow-lg focus:outline-none focus:ring-2 focus:ring-gold/60"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
              <DialogHeader>
                <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-1">
                  {openItem.kind === "onsite" ? "Amenity" : "Attraction"}
                </div>
                <DialogTitle className="font-serif text-3xl text-foreground">
                  {openItem.item.label}
                </DialogTitle>
                {openItem.item.description && (
                  <DialogDescription className="text-foreground/70 font-light whitespace-pre-line text-left">
                    {openItem.item.description}
                  </DialogDescription>
                )}
              </DialogHeader>
              {openItem.item.gallery.length > 0 && (
                <div className="gallery-zoom-lock mt-4 space-y-4">
                  {openItem.item.gallery.map((img, i) => (
                    <img
                      key={img.id}
                      src={img.url}
                      alt={`${openItem.item.label} ${i + 1}`}
                      className="w-full rounded-md border border-foreground/10"
                      loading="lazy"
                      draggable={false}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ItemCard({
  label,
  cover,
  onClick,
}: {
  label: string;
  cover: string | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative block w-full overflow-hidden rounded-xl border border-foreground/10 bg-muted aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-gold/60 active:scale-[0.99] transition-transform"
    >
      {cover ? (
        <img
          src={cover}
          alt={label}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
          draggable={false}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/10 to-foreground/5" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-left">
        <h4 className="font-serif text-2xl sm:text-3xl text-foreground text-shadow-soft">
          {label}
        </h4>
      </div>
    </button>
  );
}

const ThingsToDoCategoryPage = memo(ThingsToDoCategoryPageImpl);
export default ThingsToDoCategoryPage;
